<template>
  <TimeRange
    ref="timeRange"
    class="flex justify-between"
    :blocked-time-slots="blockedTimeSlots"
    :min-value="roomOpenHours[0]"
    :max-value="roomOpenHours[1]"
    :available-time-slots="roomOpenHours"
    :time-format="l10n.timeFormat"
    :input-classes="[
      'w-1/2 mr-2',
      'w-1/2 ml-2',
    ]"
    :model-value="modelValue"
    select-time-from-before-time-to
    blocked-time-slot-label="Booked"
    id-from="time_from"
    id-to="time_to"
    data-test-id-from="enquiry-form__time-from"
    data-test-id-to="enquiry-form__time-to"
    @update:modelValue="update"
    @selectClick="(field) => $emit('selectClick', field)"
  />
</template>

<script>
import TimeRange from '@/components/Ui/Forms/TimeRange.vue';
import {
  getBlockedTimeSlots,
  getDefaultEventDurationSlots,
  getMaximumSelectableToSlot,
  getRoomOpenHours,
  HIGHEST_TIME_SLOT,
} from '@/services/hours-map.js';

const parseTimeSlot = (value) => {
  const slot = parseInt(value, 10);

  return Number.isNaN(slot) ? null : slot;
};

export default {
  name: 'TimeFields',
  emits: ['update:modelValue', 'selectClick'],
  components: { TimeRange },

  props: {
    // eslint-disable-next-line vue/require-default-prop
    modelValue: {
      type: Array,
      validator: value => value.length === 2,
    },
    date: {
      type: String,
      default: null,
    },
    room: {
      type: Object,
      required: true,
    },
    l10n: {
      type: Object,
      required: true,
    },
  },

  computed: {
    roomOpenHours() {
      return getRoomOpenHours(this.date, this.room.availability);
    },

    blockedTimeSlots() {
      return getBlockedTimeSlots(this.date, this.room.availability);
    },
  },

  methods: {
    update([from, to]) {
      const timeFrom = parseTimeSlot(from);
      const timeTo = parseTimeSlot(to);
      const isTimeFromChanged = timeFrom !== this.modelValue[0];

      const finalTimeTo = isTimeFromChanged ? this.resolveTimeToAfterTimeFromChange(timeFrom, timeTo) : timeTo;

      this.$emit('update:modelValue', [timeFrom, finalTimeTo], isTimeFromChanged ? 'time_from' : 'time_to');
    },

    // "To" is prefilled only when empty and cleared only when it can no longer follow "From";
    // a duration the user picked deliberately must survive a "From" change.
    resolveTimeToAfterTimeFromChange(timeFrom, currentTimeTo) {
      if (timeFrom === null) {
        return null;
      }
      if (currentTimeTo === null) {
        return this.getPrefilledTimeTo(timeFrom);
      }
      const isTimeToStillSelectable = timeFrom < currentTimeTo
        && currentTimeTo <= this.getMaximumSelectableTimeTo(timeFrom);

      return isTimeToStillSelectable ? currentTimeTo : null;
    },

    getPrefilledTimeTo(timeFrom) {
      const prefilledTimeTo = Math.min(
        timeFrom + getDefaultEventDurationSlots(this.room),
        this.getMaximumSelectableTimeTo(timeFrom),
      );

      return Math.max(prefilledTimeTo, timeFrom + 1);
    },

    getMaximumSelectableTimeTo(timeFrom) {
      const closingSlot = this.roomOpenHours[1] ?? HIGHEST_TIME_SLOT;

      return getMaximumSelectableToSlot(this.roomOpenHours, this.blockedTimeSlots, timeFrom, closingSlot);
    },
  },

  mounted() {
    const notOpenDuringSavedHours = this.modelValue[0] < this.roomOpenHours[0] || this.roomOpenHours[1] < this.modelValue[1];
    const bookedDuringSavedHours = this.blockedTimeSlots.find(([timeFrom, timeTo]) => {
      return (timeFrom <= this.modelValue[0] && this.modelValue[0] <= timeTo)
        || (timeFrom <= this.modelValue[1] && this.modelValue[1] <= timeTo)
        || (this.modelValue[0] <= timeFrom && timeTo <= this.modelValue[1]);
    });
    if (notOpenDuringSavedHours || bookedDuringSavedHours) {
      this.$emit('update:modelValue', [null, null], false);
    }
  },
};
</script>

<style lang="scss" scoped>

</style>
